import { IconClose } from "@ui/Icons/IconClose";
import { IconLocate } from "@ui/Icons/IconLocate";
import type * as React from "react";

export type MapQuickAction = {
	id: string;
	label: string;
	icon?: React.ReactNode;
	onClick: () => void;
};

export type MapActiveChip = {
	id: string;
	label: string;
	onRemove: () => void;
};

type Props = {
	quickActions: MapQuickAction[];
	activeChips: MapActiveChip[];
	onClearAll: () => void;
	clearAllLabel: string;
	lowResults?: {
		message: string;
		widenLabel: string;
		onWiden: () => void;
	};
	onLocate?: () => void;
	locateLabel?: string;
};

export function MapChrome({
	quickActions,
	activeChips,
	onClearAll,
	clearAllLabel,
	lowResults,
	onLocate,
	locateLabel = "Mijn locatie",
}: Props) {
	const hasFilters = activeChips.length > 0;

	return (
		<div className="pointer-events-none absolute inset-0">
			<div className="pointer-events-auto absolute inset-x-5 top-5 flex flex-wrap items-center gap-2">
				{hasFilters ? (
					<>
						{activeChips.map((chip) => (
							<button
								key={chip.id}
								type="button"
								onClick={chip.onRemove}
								className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-[var(--radius-pill)] bg-[var(--accent-500)] px-3.5 py-2 text-[13px] font-bold text-white shadow-[var(--shadow-float)]"
							>
								{chip.label}
								<IconClose size={11} color="rgb(255 255 255 / 0.8)" />
							</button>
						))}
						<button
							type="button"
							onClick={onClearAll}
							className="inline-flex items-center rounded-[var(--radius-pill)] bg-[var(--surface-default)] px-3.5 py-2 text-[13px] font-bold shadow-[var(--shadow-float)]"
						>
							{clearAllLabel}
						</button>
					</>
				) : (
					quickActions.map((action) => (
						<button
							key={action.id}
							type="button"
							onClick={action.onClick}
							className="inline-flex items-center gap-2 rounded-[var(--radius-pill)] bg-[var(--surface-default)] px-3.5 py-2 text-[13px] font-bold shadow-[var(--shadow-float)]"
						>
							{action.icon}
							{action.label}
						</button>
					))
				)}
			</div>

			{lowResults && (
				<div className="pointer-events-auto absolute bottom-6 left-1/2 flex -translate-x-1/2 items-center gap-3.5 rounded-[var(--radius-sm)] bg-[var(--surface-muted)] px-4 py-3 shadow-[var(--shadow-dropdown)]">
					<span className="text-[13px] font-semibold text-[var(--content-secondary)]">
						{lowResults.message}
					</span>
					<button
						type="button"
						onClick={lowResults.onWiden}
						className="whitespace-nowrap text-[13px] font-extrabold"
						style={{ fontFamily: "var(--font-display)" }}
					>
						{lowResults.widenLabel}
					</button>
				</div>
			)}

			{onLocate && (
				<button
					type="button"
					onClick={onLocate}
					aria-label={locateLabel}
					className="pointer-events-auto absolute bottom-6 right-6 grid h-[46px] w-[46px] place-items-center rounded-[var(--radius-pill)] bg-[var(--surface-default)] shadow-[var(--shadow-fab)]"
				>
					<IconLocate size={21} />
				</button>
			)}
		</div>
	);
}
